import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../../components/layout/Header';
import ToggleSwitch from '../../components/customComponents/toggleSwitch/ToggleSwitch';
import {
  createManufacturer,
  getManufacturerProfile,
  updateManufacturerProfile,
  getAllDebitCategories,
  createDebitCategory,
  updateDebitCategory,
  deleteDebitCategory,
} from '../../store/actions';
import { formatINR, initials } from '../../constants/garmentCodes';
import { uploadFileToMinio } from '../../customHooks/S3Upload';

const SECTIONS = [
  'Organisation Profile',
  'Commercial Defaults',
  'Debit Categories',
  // 'Board Configuration',
  // 'Payment Instructions',
  // 'Notification Templates',
  // 'Security',
];
const PROFILE_FIELDS = ['name', 'legalName', 'gstNumber', 'currency', 'timeZone', 'logo'];
const COMMERCIAL_FIELDS = [
  ['enrolmentFee', 'Enrolment Fee (INR)'],
  ['openingBalanceRequirement', 'Opening Balance Requirement'],
  ['lowBalanceThreshold', 'Low-Balance Threshold'],
  ['debitApprovalThreshold', 'Debit Approval Threshold'],
  ['defaultMOQ', 'Default MOQ'],
  ['defaultSampleCount', 'Default Sample Count'],
];
const SECURITY_FIELDS = [
  ['requireTwoFactor', 'Require Two-Factor Authentication'],
  ['enforceStrongPassword', 'Enforce Strong Password Policy'],
  ['sessionTimeout', 'Session Timeout (60 min)'],
];
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#1b3a6b]';
const pick = (obj, keys) => Object.fromEntries(keys.map((k) => [k, obj?.[k] ?? '']));
const toNumbers = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === '' ? null : Number(v)]));

function ProfileSection({ profile, onSave }) {
  const [form, setForm] = useState(() => pick(profile, PROFILE_FIELDS));
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadFileToMinio(file, 'logos');
      if (url) setForm((f) => ({ ...f, logo: url }));
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-[15px] font-bold text-gray-900">Organisation Profile</h2>
      <div className="flex items-center gap-4 mb-4">
        {form.logo ? (
          <img src={form.logo} alt="" className="w-16 h-16 rounded-xl object-cover" />
        ) : (
          <div className="w-16 h-16 bg-[#1b3a6b] rounded-xl flex items-center justify-center text-white text-2xl font-bold">{initials(form.name || 'M')}</div>
        )}
        <div>
          <p className="text-[13px] font-bold text-gray-900">{form.name || 'Manufacturer'}</p>
          <label className="text-[12px] text-blue-600 font-medium mt-0.5 hover:underline cursor-pointer inline-block">
            {uploadingLogo ? 'Uploading…' : 'Change logo'}
            <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo} onChange={handleLogoChange} />
          </label>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Legal Name</label>
          <input value={form.legalName} onChange={set('legalName')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Trading Name</label>
          <input value={form.name} onChange={set('name')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">GST Number</label>
          <input value={form.gstNumber} onChange={set('gstNumber')} className={`${inputCls} font-mono`} />
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Default Currency</label>
          <input value={form.currency} onChange={set('currency')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Time Zone</label>
          <select value={form.timeZone} onChange={set('timeZone')} className={inputCls}>
            <option value="Asia/Kolkata (IST)">Asia/Kolkata (IST)</option>
            <option value="Asia/Dubai (GST)">Asia/Dubai (GST)</option>
            <option value="Europe/London (GMT)">Europe/London (GMT)</option>
            <option value="America/New_York (EST)">America/New_York (EST)</option>
          </select>
        </div>
      </div>
      <div className="pt-2">
        <button onClick={() => onSave(form)} className="px-5 py-2 text-[13px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Save Changes</button>
      </div>
    </div>
  );
}

function CommercialSection({ profile, onSave }) {
  const [form, setForm] = useState(() => pick(profile, COMMERCIAL_FIELDS.map(([k]) => k)));
  return (
    <div className="space-y-5">
      <h2 className="text-[14px] font-semibold text-gray-900">Commercial Defaults</h2>
      <div className="grid grid-cols-2 gap-4">
        {COMMERCIAL_FIELDS.map(([k, text]) => (
          <div key={k}>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">{text}</label>
            <input type="number" min="0" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={`${inputCls} font-mono`} />
          </div>
        ))}
      </div>
      <button onClick={() => onSave(toNumbers(form))} className="px-4 py-2 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Save Changes</button>
    </div>
  );
}

function DebitCategoriesSection({ manufacturerId }) {
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.debitcategory.categories);
  const loading = useSelector((state) => state.debitcategory.loading);
  const [form, setForm] = useState({ name: '', threshold: '', attachmentRequired: false, brandApproval: false });

  useEffect(() => {
    dispatch(getAllDebitCategories({ manufacturerId }));
  }, [dispatch, manufacturerId]);

  const add = (e) => {
    e.preventDefault();
    dispatch(
      createDebitCategory({
        payload: {
          manufacturerId,
          name: form.name.trim(),
          threshold: form.threshold === '' ? null : Number(form.threshold),
          attachmentRequired: form.attachmentRequired,
          brandApproval: form.brandApproval,
          active: true,
        },
      }),
    );
    setForm({ name: '', threshold: '', attachmentRequired: false, brandApproval: false });
  };

  const toggle = (cat, key) => dispatch(updateDebitCategory({ payload: { id: cat.id, [key]: !cat[key] } }));

  return (
    <div className="space-y-4">
      <h2 className="text-[14px] font-semibold text-gray-900">Debit Categories</h2>
      <form onSubmit={add} className="flex items-end gap-3 flex-wrap bg-gray-50 rounded-lg p-3">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Category Name</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </div>
        <div className="w-40">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Approval Threshold</label>
          <input type="number" min="0" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} className={`${inputCls} font-mono`} />
        </div>
        <label className="flex items-center gap-1.5 text-[12px] text-gray-700 pb-2"><input type="checkbox" checked={form.attachmentRequired} onChange={(e) => setForm({ ...form, attachmentRequired: e.target.checked })} /> Attachment</label>
        <label className="flex items-center gap-1.5 text-[12px] text-gray-700 pb-2"><input type="checkbox" checked={form.brandApproval} onChange={(e) => setForm({ ...form, brandApproval: e.target.checked })} /> Brand approval</label>
        <button type="submit" className="px-3 py-2 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">+ Add</button>
      </form>
      {loading && <p className="text-[11px] text-gray-400">Loading…</p>}
      <table className="w-full data-table">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="px-3 py-2 text-left">Category</th>
            <th className="px-3 py-2 text-center">Attachment Required</th>
            <th className="px-3 py-2 text-center">Brand Approval</th>
            <th className="px-3 py-2 text-right">Approval Threshold</th>
            <th className="px-3 py-2 text-center">Active</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {categories.map((cat) => (
            <tr key={cat.id} className={`hover:bg-gray-50 ${cat.__optimistic ? 'opacity-60' : ''}`}>
              <td className="px-3 py-2 text-[12px] font-medium text-gray-800">{cat.name}</td>
              <td className="px-3 py-2 text-center"><ToggleSwitch checked={!!cat.attachmentRequired} onChange={() => toggle(cat, 'attachmentRequired')} /></td>
              <td className="px-3 py-2 text-center"><ToggleSwitch checked={!!cat.brandApproval} onChange={() => toggle(cat, 'brandApproval')} /></td>
              <td className="px-3 py-2 text-right font-mono text-[12px] text-gray-600">{cat.threshold != null ? formatINR(cat.threshold) : '—'}</td>
              <td className="px-3 py-2 text-center"><ToggleSwitch checked={!!cat.active} onChange={() => toggle(cat, 'active')} /></td>
              <td className="px-3 py-2 text-right">
                {!cat.__optimistic && <button onClick={() => dispatch(deleteDebitCategory({ id: cat.id }))} className="text-[11px] text-gray-400 hover:text-red-600">Delete</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && categories.length === 0 && <p className="text-center text-[12px] text-gray-400 py-6">No debit categories yet</p>}
    </div>
  );
}

function SecuritySection({ profile, onSave }) {
  const [form, setForm] = useState(() => pick(profile, SECURITY_FIELDS.map(([k]) => k)));
  return (
    <div className="space-y-6">
      <h2 className="text-[15px] font-bold text-gray-900">Security Settings</h2>
      <div className="space-y-4">
        {SECURITY_FIELDS.map(([k, text]) => (
          <div key={k} className="flex items-center justify-between px-5 py-4 bg-gray-50/70 rounded-lg">
            <span className="text-[13px] text-gray-600 font-medium">{text}</span>
            <ToggleSwitch checked={!!form[k]} onChange={() => setForm({ ...form, [k]: !form[k] })} />
          </div>
        ))}
      </div>
      <div className="pt-2">
        <button onClick={() => onSave(form)} className="px-5 py-2 text-[13px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Save Changes</button>
      </div>
    </div>
  );
}

function PlaceholderSection({ title, desc }) {
  return (
    <div className="space-y-4">
      <h2 className="text-[15px] font-bold text-gray-900">{title}</h2>
      <div className="p-8 border border-dashed border-gray-200 rounded-lg text-center">
        <p className="text-[13px] text-gray-500">{desc}</p>
      </div>
    </div>
  );
}

function CreateProfile() {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ name: '', legalName: '', gstNumber: '', currency: 'INR', timeZone: 'Asia/Kolkata' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    dispatch(createManufacturer({ payload: { ...form, status: 'Active' }, callback: { success: () => dispatch(getManufacturerProfile()) } }));
  };
  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <h2 className="text-[15px] font-bold text-gray-900 mb-1">Set up your manufacturer profile</h2>
        <p className="text-[13px] text-gray-500">No manufacturer profile exists for your company yet. Create it to start adding brands and engagements.</p>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div><label className="block text-[12px] text-gray-500 mb-1.5">Legal Name</label><input value={form.legalName} onChange={set('legalName')} className={inputCls} /></div>
        <div><label className="block text-[12px] text-gray-500 mb-1.5">Trading Name *</label><input required value={form.name} onChange={set('name')} className={inputCls} /></div>
        <div><label className="block text-[12px] text-gray-500 mb-1.5">GST Number</label><input value={form.gstNumber} onChange={set('gstNumber')} className={`${inputCls} font-mono`} /></div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Default Currency</label>
          <input value={form.currency} onChange={set('currency')} className={inputCls} />
        </div>
        <div>
          <label className="block text-[12px] text-gray-500 mb-1.5">Time Zone</label>
          <select value={form.timeZone} onChange={set('timeZone')} className={inputCls}>
            <option value="Asia/Kolkata (IST)">Asia/Kolkata (IST)</option>
            <option value="Asia/Dubai (GST)">Asia/Dubai (GST)</option>
            <option value="Europe/London (GMT)">Europe/London (GMT)</option>
            <option value="America/New_York (EST)">America/New_York (EST)</option>
          </select>
        </div>
      </div>
      <div className="pt-2">
        <button type="submit" className="px-5 py-2 text-[13px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Create Profile</button>
      </div>
    </form>
  );
}

export default function ManufacturerSettings() {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.manufacturer.profile);
  const [activeSection, setActiveSection] = useState(SECTIONS[0]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getManufacturerProfile({ callback: { success: () => setChecked(true), failure: () => setChecked(true) } }));
    }, 50);
    return () => clearTimeout(timer);
  }, [dispatch]);

  const save = (changes) => dispatch(updateManufacturerProfile(changes));

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <Header title="Manufacturer Settings" breadcrumbs={[{ workspace: true }, { label: 'Settings' }]} />
      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <div className="flex gap-6">
          <div className="w-48 flex-shrink-0">
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveSection(s)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[12px] font-medium transition-colors ${activeSection === s ? 'bg-[#1b3a6b] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {s}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex-1 bg-white rounded-lg border border-gray-100 p-6">
            {!checked ? (
              <p className="text-[12px] text-gray-400">Loading…</p>
            ) : !profile ? (
              activeSection === 'Organisation Profile' ? (
                <CreateProfile />
              ) : (
                <div className="p-8 border border-dashed border-gray-200 rounded-lg text-center">
                  <p className="text-[13px] text-gray-500">Please create your organisation profile first.</p>
                </div>
              )
            ) : (
              <>
                {activeSection === 'Organisation Profile' && <ProfileSection key={profile.id} profile={profile} onSave={save} />}
                {activeSection === 'Commercial Defaults' && <CommercialSection key={profile.id} profile={profile} onSave={save} />}
                {activeSection === 'Debit Categories' && <DebitCategoriesSection manufacturerId={profile.id} />}
                {/* {activeSection === 'Board Configuration' && <PlaceholderSection title="Board Configuration" desc="Configure your Kanban boards and workflows here." />} */}
                {/* {activeSection === 'Payment Instructions' && <PlaceholderSection title="Payment Instructions" desc="Set up default payment instructions and accepted methods for your brands." />} */}
                {/* {activeSection === 'Notification Templates' && <PlaceholderSection title="Notification Templates" desc="Customise email and in-app notification templates." />} */}
                {/* {activeSection === 'Security' && <SecuritySection key={profile.id} profile={profile} onSave={save} />} */}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
